import React, { Component } from 'react'

import { Link } from 'react-router-dom'

import Loading from '../../components/shared/Loading'

import { ResetPasswordForm } from './ResetPasswordForm'
import { Message } from './Message'

import { verifyRecoveryToken, resetPassword } from '../../../lib/authentication'

import { isComplexPassword, createPasswordComplexityList } from '../complexity'
import { createIdentityMap, getPossibleRecoveryToken, getPasswordPolicyComplexity } from '../utils'

import routes from '../../constants/routes'

import './auth.scss'

const Stages = createIdentityMap(
  'VERIFYING_RECOVERY_TOKEN',
  'INVALID_RECOVERY_TOKEN',
  'ENTERING_NEW_PASSWORD',
  'SUBMITTING_NEW_PASSWORD',
  'VALID_NEW_PASSWORD',
  'INVALID_NEW_PASSWORD',
)

/**
 * @todo - Since resetPassword api call returns a sessionToken which we
 * immediately pass to oktaAuth.redirect, the messaging in renderValidMessage
 * _at best_ flashes into view before disappearing due to the redirect to
 * /implicit/callback.
 *
 * So best bet is to just remove the clause to display the valid message
 * (oh, and no need to connect this component and dispath `setAuth`, since
 * that'll happen by virtue of hitting /implicit/callback)
 */

export default class ResetPassword extends Component {
  constructor(props) {
    super(props)

    this.state = {
      newPassword: '',
      confirmPassword: '',
      passwordComplexity: {},
      username: '',
      error: { errorSummary: '', errorItems: [] },
      stage: Stages.VERIFYING_RECOVERY_TOKEN,
    }
  }

  componentDidMount() {
    this.handleVerifyRecoveryToken()
  }

  get recoveryToken() {
    return getPossibleRecoveryToken(this.props.location)
  }

  setPasswordComplexity(passwordComplexity = {}) {
    this.setState({ passwordComplexity })
  }

  setUsername(username = '') {
    this.setState({ username })
  }

  setError(errorSummary, ...errorItems) {
    this.setState({ error: { errorSummary, errorItems } })
  }

  setStage(stage) {
    this.setState({ stage })
  }

  isOnStage(...stages) {
    return stages.includes(this.state.stage)
  }

  getComplexityList = () => {
    const { username, newPassword, passwordComplexity } = this.state
    return createPasswordComplexityList(passwordComplexity, newPassword, username)
  }

  handleChange = (event) => {
    this.setState({
      [event.target.id]: event.target.value
    })
  }

  //TODO: If token is not valid, we (currently) display a message stating as such
  handleVerifyRecoveryToken = async () => {
    this.setStage(Stages.VERIFYING_RECOVERY_TOKEN)
    const { username, policy } = await verifyRecoveryToken(this.recoveryToken)
    const isVerified = !!policy

    isVerified && this.setPasswordComplexity(getPasswordPolicyComplexity(policy))
    this.setUsername(username)
    this.setStage(isVerified && Stages.ENTERING_NEW_PASSWORD || Stages.INVALID_RECOVERY_TOKEN)
  }

  //TODO: Well crap, "logged in" in this app means "state.user is not empty"
  // Which we can't do anything about.
  handleSubmitForm = async (event) => {
    event.preventDefault()

    this.setError()
    this.setStage(Stages.SUBMITTING_NEW_PASSWORD)

    const { success, error } = await resetPassword(this.recoveryToken, this.state.newPassword)

    if (!success && error) {
      const { errorSummary = '', errorItems = [] } = error
      this.setError(errorSummary, ...errorItems)
      this.setStage(Stages.INVALID_NEW_PASSWORD)
      return
    }

    if (success && !error) {
      this.setStage(Stages.VALID_NEW_PASSWORD)
      this.props.setAuth()
      return
    }

    this.setError('Unknown Error occurred, please try again')
    this.setStage(Stages.ENTERING_NEW_PASSWORD)
  }

  validateForm = () => {
    const { username, newPassword, confirmPassword, passwordComplexity } = this.state
    const isPasswordMatch = newPassword === confirmPassword
    return isComplexPassword(passwordComplexity, newPassword, username) && isPasswordMatch
  }

  renderForm() {
    const { newPassword, confirmPassword } = this.state
    return (
      <ResetPasswordForm
        title="Choose a New Password"
        onSubmit={this.handleSubmitForm}
        onChange={this.handleChange}
        validateForm={this.validateForm}
        complexityList={this.getComplexityList}
        newPassword={newPassword}
        confirmPassword={confirmPassword}
      >
        { this.isOnStage(Stages.INVALID_NEW_PASSWORD) && this.renderErrors() }
      </ResetPasswordForm>
    )
  }

  renderErrors() {
    const { error: { errorSummary = 'Unknown Error Ocurred', errorItems = [] } = {} } = this.state
    return (
      <Message title={errorSummary} type="danger">
        { errorItems.map(errorItem => (<p key={btoa(errorItem)}>{errorItem}</p>)) }
      </Message>
    )
  }

  renderInvalidTokenMessage() {
    return (
      <Message title="Invalid Recovery Token" type="info">
        <p>If you are able to, please try <Link to={routes.LOGIN}>logging in</Link></p>
        <p>Alternatively, you can get a new recovery token emailed to you by using the <Link to={routes.FORGOT_PASSWORD}>forgot password</Link> form</p>
        <p>If none of the above work out, please contact support for help!</p>
      </Message>
    )
  }

  renderValidMessage() {
    return (
      <Message title="Your Password Has Been Reset" type="success">
        <p>You&apos;re all set! You can now <Link to={routes.SUBMISSIONS}>browse your submissions</Link></p>
        <p>If for some reason you are still not signed in, try <Link to={routes.LOGIN}>logging in with your new password</Link></p>
      </Message>
    )
  }

  render() {
    return (
      <div className="AuthContainer container vertical">
        { this.isOnStage(Stages.VERIFYING_RECOVERY_TOKEN, Stages.SUBMITTING_NEW_PASSWORD) && <Loading classes="loading" /> }
        { this.isOnStage(Stages.ENTERING_NEW_PASSWORD, Stages.INVALID_NEW_PASSWORD) && this.renderForm() }
        { this.isOnStage(Stages.VALID_NEW_PASSWORD) && this.renderValidMessage() }
        { this.isOnStage(Stages.INVALID_RECOVERY_TOKEN) && this.renderInvalidTokenMessage() }
      </div>
    )
  }
}

